<template>
	<template v-if="loadState === 'loaded'">
		<force-infos v-if="forceInfo" :responsibles="[ env.user.id ]" :src-type="srcType" :src-id="id"></force-infos>
		<object-layout :name="saveName" :subviews="subviews" :build-url="addUrlParams">
			<template #hero><slot name="hero"></slot></template>
			<ui-root :part="form" :data="data" :display="{ mode: 'form' }" :errs="errors" :actions="controls" :readonly="ro" ref="formComponent"></ui-root>
		</object-layout>
	</template>
	<dialog v-else-if="isWarning" class="mode-form warning-dialog">
		<div class="dialog-head">
			<div class="dialog-head-left"><sym-icon :name="warning.icon"></sym-icon> {{ warning.title }}</div>
		</div>
		<div class="dialog-body">
			<div>{{ warning.body }}</div>
			<div v-if="helpers403.length > 0" class="helpers403">
				<label for="usersWithAccess">{{ env.msg('users.with.access') }}:</label>
				<select id="usersWithAccess">
					<option v-for="h in helpers403" :key="h.id" :value="h.id">{{ h.name }} ({{ userTags(h) }})</option>
				</select>
			</div>
		</div>
		<div class="dialog-foot">
			<div class="dialog-foot-left"><button type="button" class="sbutton" @click="goBack"><sym-icon name="caret-left-16"></sym-icon> {{ env.msg('butt.back')}}</button></div>
			<div class="dialog-foot-center"></div>
			<div class="dialog-foot-right"></div>
		</div>
	</dialog>
</template>

<script setup lang="ts">
import {ref, computed, watch, reactive, provide, nextTick, onUnmounted, onMounted} from "vue";
import ObjectLayout from "@/ui/subview/ObjectLayout.vue";
import {SubviewBadgeInfo, SubviewBadgePayload, SubviewTab} from "@t/ui/SubView.ts";
import {loadFormDefinition, loadSourceType, setEndpointId, SourceType} from "@t/meta/SourceType.ts";
import {injectEnv, provideNewEventBus} from "@fd/Environment.ts";
import {dialogControls, FormDefinition} from "@t/fd/layout/FormDefinition.ts";
import {addContextParams, AppContextMap, contextBuilder, contextKey, injectContext} from "@t/fd/AppContext.ts";
import {DataWithMeta, removeMeta} from "@t/fd/WithMeta.ts";
import type {FormData} from "@t/fd/FormPart.ts";
import FormPart from "@fd/containers/FormPart.vue";
import type {ErrorMap} from "@fd/PartInjectKeys.ts";
import type { FormPart as FormPartType } from "@t/fd/FormPart.ts";
import { useRoute, useRouter } from "vue-router";
import {getAttParam} from "@/router/Router.ts";
import {ApiError, ApiErrorResponse, HelpingUser} from "@t/util/ApiError.ts";
import {useNavigationStore} from "@/store/NavigationStore.ts";
import get from "lodash.get";
import ForceInfos from "@/processes/ForceInfos.vue";
import {useForceInfoStore} from "@/store/ForceInfoStore.ts";
import {checkAccessKey, hasAccess, ObjectAccessFlag, readOnlyKey} from "@t/security/ObjectAccess.ts";
import SymIcon from "@fd/helpers/SymIcon.vue";
import {InterfaceRoot} from "@t/fd/InterfaceRoot.ts";
import UiRoot from "@fd/containers/UiRoot.vue";


const props = withDefaults(defineProps<{
	srcType: number;
	srcSubType?: number;
	name?: string;
	buttons?: string[];
	idParam?:string;

	/* handlers to "hack" behavior */
	buildEndpoint?: (endpoint:string, edit:boolean, type:SourceType) => string;
	extraErrors?: ErrorMap;
	/** Display force info component */
	forceInfo?: boolean;
	/** Can be used to alter subview URL (if not component based) */
	buildUrl?: (tab:SubviewTab) => string;
	/** Parent component can add extra params to /api/subviews/badges endpoint call */
	buildBadgeUrl?: (endpoint:string) => string;
}>(), {
	buttons: () => ["save", "saveAndBack"],
	idParam: "id",
	buildEndpoint: (endpoint:string, e:boolean, t:SourceType) => endpoint,
	extraErrors: () => [],
	forceInfo: false
});

const emit = defineEmits(['edit', 'create', 'replace', "beforesave", 'problems', 'notfound', 'forbidden', 'error']);

const env = injectEnv();
const ns = useNavigationStore();
const route = useRoute();
const router = useRouter();
const fis = useForceInfoStore();

const type = ref<SourceType>(null);
const form = ref<InterfaceRoot<FormDefinition>>(null);
const formComponent = ref<InstanceType<typeof FormPart> | null>(null);
const data = defineModel<FormData>();
const badges = reactive<SubviewBadgePayload>([]);

const errors:ErrorMap = reactive<ErrorMap>([...props.extraErrors]);
const backendErrors: ErrorMap = reactive<ErrorMap>([]);
watch(
	[backendErrors, () => props.extraErrors],
	() => { errors.splice(0, errors.length, ...backendErrors, ...props.extraErrors); },
	{ deep: true }
);

const ro = computed<boolean>(() => {
	const access = hasAccess(data.value, "edit");
	return !access || fis.userBlocks;
});

function checkAccess(...flags:ObjectAccessFlag[]):boolean {
	return hasAccess(data.value, ...flags);
}

provide(readOnlyKey, ro);
provide(checkAccessKey, checkAccess);

const controls = computed<FormPartType>(() => {
	return dialogControls(env, props.buttons);
});
const bus = provideNewEventBus();

type LoadingState = "initial" | "loading" | "loaded" | "notfound" | "forbidden" | "error";
const loadState = ref<LoadingState>("initial");
const helpers403 = reactive<HelpingUser[]>([]);

const subviews = computed<SubviewTab[]>(() => {
	if (type.value && Array.isArray(type.value.subviews)) {
		const all = type.value.subviews;
		const filtered:SubviewTab[] = [];
		for (let sub of all) {
			if (!sub.show || (!hasId.value && !sub.showOnCreate)) continue;
			const subview = { ...sub };
			const bi = findBadgeFor(sub.name);
			if (bi?.badge) {
				subview.badge = bi.badge;
			}
			filtered.push(subview);
		}
		return filtered;
	} else return [];
});

function userTags(u:HelpingUser):string {
	const tags:string[] = [];
	if (u.owner) tags.push(env.msg('user.role.owner'));
	if (u.salesman) tags.push(env.msg('user.role.salesman'));
	if (u.responsible) tags.push(env.msg('user.role.responsible'));
	if (u.admin) tags.push(env.msg('user.role.admin'));
	return tags.join(', ');
}

interface WarningInfo {
	icon: string;
	title: string;
	body: string;
}

const warning = computed<WarningInfo>(() => {
	if (loadState.value === 'notfound') {
		return { icon: "not-found", title: env.msg('record.notFound'), body: env.msg('record.notFound.desc', id.value) };
	} else if (loadState.value === 'forbidden') {
		return { icon: "forbidden", title: env.msg('record.forbidden'), body: env.msg('record.forbidden.desc', id.value) };
	} else if (loadState.value === 'error') {
		return { icon: "heart", title: env.msg('error.internal'), body: env.msg('error.internal.desc') };
	}
	return null;
});
const isWarning = computed<boolean>(() => warning.value != null);

const saveName = computed<string>(() => props.name ?? ("object-"+props.srcType));
const id = computed<string|null>(() => {
	const val = getAttParam(route, props.idParam);
	if (val == null) return null;
	else if (Array.isArray(val)) return val[0];
	return val;
});
const hasId = computed<boolean>(() => (typeof id.value === "string" && id.value.length > 0 && id.value !== "0"));

const parentContext = injectContext();
const ctx = computed<AppContextMap>(() => {
	if (form.value && data.value) {
		return contextBuilder(form.value, data.value, parentContext.value);
	} else {
		return parentContext.value;
	}
});
provide(contextKey, ctx);


function addUrlParams(tab:SubviewTab):string {
	let url = typeof props.buildUrl === "function" ? props.buildUrl(tab) : tab.src;
	url = env.net.urlParam(url, "subSrcType", props.srcType);
	if (props.srcSubType) url = env.net.urlParam(url, "subSrcSubType", props.srcSubType);
	if (hasId.value) url = env.net.urlParam(url, "subSrcId", id.value);
	return url;
}

function buildEndpoint():string {
	let ep;
	if (hasId.value) {
		ep = setEndpointId("api"+type.value.edit, id.value);
	} else {
		ep = "api" + type.value.init;
		const initCtx = {...ctx.value };
		for (const [key, value] of Object.entries(route.query)) {
			if (Array.isArray(value)) {
				if (value.length > 0) initCtx[key] = value[0];
			} else {
				initCtx[key] = value;
			}
		}

		ep = addContextParams(ep, initCtx);
	}
	ep = props.buildEndpoint(ep, hasId.value, type.value);
	return ep;
}

watch([() => props.srcType, () => props.srcSubType, () => id.value], async () => {
	loadState.value = "loading";
	helpers403.length = 0;
	try {
		clearErrors();
		ns.chatContext = null;
		if (type.value == null || type.value.id !== props.srcType) {
			type.value = await loadSourceType(env, props.srcType);
		}
		const metaSupport = !!type.value.meta;	// If endpoints support metadata they can load data and corresponding form at once
		let ep = buildEndpoint();

		if (metaSupport) ep = env.net.urlParam(ep, "meta", "true");
		else form.value = await loadFormDefinition(env, type.value.form);

		const resp = await env.net.fetch(ep);
		if (resp.ok) {
			if (metaSupport) {
				const dataMeta:DataWithMeta<FormData> = await resp.json();
				form.value = dataMeta.META.form;
				data.value = removeMeta(dataMeta);
			} else {
				data.value = await resp.json();
			}
			loadState.value = "loaded";
			await nextTick();
			if (hasId.value) {
				emit('edit', id.value);
				ns.display(type.value.id, id.value);
				await loadSubviewBadges(props.srcType, props.srcSubType, id.value);
			} else emit('create');
		} else if (resp.status === 403 /* Forbidden */) {
			loadState.value = "forbidden";
			const apiErr:ApiErrorResponse = await resp.json();
			if (Array.isArray(apiErr.helpers)) helpers403.push(...apiErr.helpers);
			ns.setEmptyMenu();
			ns.forward(env.msg('record.forbidden'));
			emit('forbidden');
		} else if (resp.status === 404 /* Not found */) {
			loadState.value = "notfound";
			ns.setEmptyMenu();
			ns.forward(env.msg('record.notFound'));
			emit('notfound');
		} else {
			console.error("Cannot load form data with META info from " + ep);
			loadState.value = "error";
			emit('error', ep);
		}
	} catch (ex) {
		loadState.value = "error";
		emit('error', ex);
	}
}, { immediate: true });


async function loadSubviewBadges(srcType: number, srcSubType: number, id: string):Promise<any> {
	if (Array.isArray(type.value.subviews)) {
		const all = type.value.subviews;
		const query:SubviewBadgeInfo[] = [];
		for (let sub of all) {
			if (sub.show && sub.counted) query.push({ name: sub.name });
		}
		if (query.length > 0) {
			let ep = env.net.urlParam("api/subviews/badges", "subSrcType", srcType);
			if (srcSubType) ep = env.net.urlParam(ep, "subSrcSubType", srcSubType);
			if (id) ep = env.net.urlParam(ep, "subSrcId", id);
			if (typeof props.buildBadgeUrl === "function") ep = props.buildBadgeUrl(ep);

			const resp = await env.net.fetch(ep, "POST", query);
			if (resp.ok) {
				const bgs:SubviewBadgePayload = await resp.json();
				badges.length = 0;
				badges.push(...bgs);
			}
		}
	}
}

function findBadgeFor(name:string):SubviewBadgeInfo {
	return badges.find((bi) => name === bi.name);
}

function getContext():AppContextMap {
	return ctx.value;
}

let saving = false;

function clearErrors():void {
	backendErrors.length = 0;
}

function processSubmitProblems(problems:ApiError[]):void {
	if (!Array.isArray(problems)) return;
	backendErrors.push(...problems);
}

const unregCommands = bus.onCommand(["save", "saveAndBack"], (cmd:string) => {
	return new Promise(async (resolve) => {
		const stay = cmd === "save";
		if (!saving) {
			try {
				saving = true;
				emit('beforesave', hasId.value);
				document.body.style.cursor = "progress";
				let ep:string, method:string;
				if (hasId.value) {
					const idFromData = get(data.value, type.value.idProperty ?? "id");
					ep = setEndpointId("api" + type.value.edit, idFromData);
					method = "PUT";
				} else {
					ep = "api" + type.value.create;
					method = "POST";
				}
				await env.net.fetch(ep, method, data.value)
					.then((resp) => {
						clearErrors();
						if (resp.ok) {
							// Successfull save ... body should be the new version of the record
							resp.json().then((record) => {
								data.value = record;
								if (stay) {
									nextTick(() => {
										// Set form to NOT-dirty
										if (formComponent.value) formComponent.value.setDirty(false);
									});
									ns.display(type.value.id, id.value);	// update history with newer (data, e.g. name)
									emit('replace');	// Start edit on the same page
								} else {
									router.back();
								}
							});
						} else {
							// Backend returned non-20xx status code (e.g. BAD_REQUEST)
							resp.json().then((er:ApiErrorResponse) => {
								emit('problems', er);
								processSubmitProblems(er.problems);
							});
						}
						resolve(!resp.ok ? "fail" : "ok");	// this will cause propper feedback to the user
					})
					.catch((err) => {
						resolve("fail");
						const errText = err.toString();
						clearErrors();
						processSubmitProblems([{ type: "ERROR", level: "SEVERE", message:errText }]);
					});
			} catch(err) {
				resolve("fail");
				const errText = err.toString();
				clearErrors();
				processSubmitProblems([{ type: "ERROR", level: "SEVERE", message:errText }]);
				console.error(err);
			} finally {
				saving = false;
				document.body.style.cursor = "auto";
			}
		} else resolve("fail");
	});
});

function goBack() {
	ns.back();
}

defineExpose({ getContext, bus });

// onMounted(() => {
// 	if (props.forceInfo) {
// 		console.log("+++ setting responsibles to fi store");
// 		fis.setResponsibles([ env.user.id ]);
// 	}
// });

onUnmounted(() => {
	ns.clearSideMenu();
	unregCommands();
});
</script>

<style>
.helpers403 {
	margin-top: 16px;
	display: flex;
	flex-direction: row;
	column-gap: 4px;
	align-items: baseline;
}
</style>